export enum AnalyticType {
    /**
     * Event only sent to analytics
     */
    ANALYTIC,

    /**
     * Event sent to analtyics and a summary sent to the Customer Messaging Service (Intercom)
     */
    CUSTOMER_MESSAGING_SUMMARY,

    /**
     * Event sent to analtyics and a detailed description sent to the Customer Messaging Service (Intercom).
     * The details include any additional labels captured for analytics
     */
    CUSTOMER_MESSAGING_DETAILED
}

/**
 * Events to be sent to google analytics
 *
 * Google analytic events follow a hierarchy of:
 *
 * - Category: Top level grouping.
 * - Action: User action performed
 * - Label: Optional label that provides lower level detail.
 *
 * The label is dynamic in nature. This is populated when capturing the event in the AnalyticsService
 */
export class AnalyticEvent {

    /**
     * Entity edit screens
     * - view: View an editable screen
     * - view-restricted: View a readonly/restricted screen
     * - add: Add a new entity
     * - update: Update an existing entity
     */
    private static readonly CATEGORY_ENTITY_EDIT = "entity-edit";
    public static readonly ENTITY_VIEW = new AnalyticEvent("view-editable", AnalyticEvent.CATEGORY_ENTITY_EDIT, AnalyticType.ANALYTIC);
    public static readonly ENTITY_VIEW_RESTRICTED = new AnalyticEvent("view-restricted", AnalyticEvent.CATEGORY_ENTITY_EDIT, AnalyticType.ANALYTIC);
    public static readonly ENTITY_ADD = new AnalyticEvent("add", AnalyticEvent.CATEGORY_ENTITY_EDIT, AnalyticType.CUSTOMER_MESSAGING_DETAILED);
    public static readonly ENTITY_EDIT = new AnalyticEvent("update", AnalyticEvent.CATEGORY_ENTITY_EDIT, AnalyticType.ANALYTIC);

    /**
     * Interactions with search and edit controls, grids, etc
     * - grid-entity-view: User clicked on a column link to view an entity in the dialog
     * - grid-entity-view-denied: User attempted to view an entity from the grid but does not have the necessary privileges
     * - grid-add-existing: Click add existing button on grid
     * - grid-add-existing: Click add new button on grid
     * - grid-quick-add: Add a record via the quick add autocomplete
     * - select-add-new: Add new entity from a select control
     * - select-extended-search: Extended search from a select control
     * - select-view-existing: View existing from a select control
     * - grid-drag-and-drop: Drag and drop reordering on a grid
     * - grid-inline-edit: A row in the grid is being edited
     * - grid-localisation: Localisation dialog has been opened from an entity grid
     * - grid-row-remove: Remove a row from a grid
     */
    private static readonly CATEGORY_ENTITY_INTERACTIONS = "control-interactions";
    public static readonly ENTITY_INTERACTION_GRID_VIEW = new AnalyticEvent("grid-entity-view", AnalyticEvent.CATEGORY_ENTITY_INTERACTIONS, AnalyticType.ANALYTIC, true, true);
    public static readonly ENTITY_INTERACTION_GRID_VIEW_NO_PRIV = new AnalyticEvent("grid-entity-view-denied", AnalyticEvent.CATEGORY_ENTITY_INTERACTIONS, AnalyticType.ANALYTIC, true, true);
    public static readonly ENTITY_INTERACTION_GRID_ADD_EXISTING = new AnalyticEvent("grid-add-existing", AnalyticEvent.CATEGORY_ENTITY_INTERACTIONS, AnalyticType.ANALYTIC, true, true);
    public static readonly ENTITY_INTERACTION_GRID_ADD_NEW = new AnalyticEvent("grid-add-new", AnalyticEvent.CATEGORY_ENTITY_INTERACTIONS, AnalyticType.ANALYTIC, true, true);
    public static readonly ENTITY_INTERACTION_GRID_QUICK_ADD = new AnalyticEvent("grid-quick-add", AnalyticEvent.CATEGORY_ENTITY_INTERACTIONS, AnalyticType.ANALYTIC, true);
    public static readonly ENTITY_INTERACTION_SELECT_ADD_NEW = new AnalyticEvent("select-add-new", AnalyticEvent.CATEGORY_ENTITY_INTERACTIONS, AnalyticType.ANALYTIC, true, true);
    public static readonly ENTITY_INTERACTION_SELECT_EXTENDED_SEARCH = new AnalyticEvent("select-extended-search", AnalyticEvent.CATEGORY_ENTITY_INTERACTIONS, AnalyticType.ANALYTIC, true, true);
    public static readonly ENTITY_INTERACTION_SELECT_VIEW_EXISTING = new AnalyticEvent("select-view-existing", AnalyticEvent.CATEGORY_ENTITY_INTERACTIONS, AnalyticType.ANALYTIC, true, true);
    public static readonly ENTITY_INTERACTION_GRID_DRAG_AND_DROP = new AnalyticEvent("grid-drag-and-drop", AnalyticEvent.CATEGORY_ENTITY_INTERACTIONS, AnalyticType.ANALYTIC, true);
    public static readonly ENTITY_INTERACTION_GRID_MOBILE_REORDERING = new AnalyticEvent("grid-mobile-reordering", AnalyticEvent.CATEGORY_ENTITY_INTERACTIONS, AnalyticType.ANALYTIC, true);
    public static readonly ENTITY_INTERACTION_GRID_INLINE_EDIT = new AnalyticEvent("grid-inline-edit", AnalyticEvent.CATEGORY_ENTITY_INTERACTIONS, AnalyticType.ANALYTIC, true);
    public static readonly ENTITY_INTERACTION_GRID_LOCALISATION = new AnalyticEvent("grid-localisation", AnalyticEvent.CATEGORY_ENTITY_INTERACTIONS, AnalyticType.ANALYTIC, true, true);
    public static readonly ENTITY_INTERACTION_GRID_ROW_REMOVE = new AnalyticEvent("grid-row-remove", AnalyticEvent.CATEGORY_ENTITY_INTERACTIONS, AnalyticType.ANALYTIC, true);


    /**
     * Dialog for extended search, edit, and view
     * - tab-depth-3: 3 tabs open
     * - tab-depth-5: 5 tabs open
     * - tab-depth-10: 10 tabs open
     * - tab-depth-15: 15 tabs open
     *
     */
    private static readonly CATEGORY_ENTITY_DIALOG = "entity-dialog";
    public static readonly ENTITY_DIALOG_TAB_3 = new AnalyticEvent("tab-depth-3", AnalyticEvent.CATEGORY_ENTITY_DIALOG, AnalyticType.ANALYTIC);
    public static readonly ENTITY_DIALOG_TAB_5 = new AnalyticEvent("tab-depth-5", AnalyticEvent.CATEGORY_ENTITY_DIALOG, AnalyticType.ANALYTIC);
    public static readonly ENTITY_DIALOG_TAB_10 = new AnalyticEvent("tab-depth-10", AnalyticEvent.CATEGORY_ENTITY_DIALOG, AnalyticType.ANALYTIC);
    public static readonly ENTITY_DIALOG_TAB_15 = new AnalyticEvent("tab-depth-15", AnalyticEvent.CATEGORY_ENTITY_DIALOG, AnalyticType.ANALYTIC);

    /**
     * Header button actions
     * - global-localisation-enabled
     * - global-localisation-disabled
     * - hamburger: Hamburger clicked
     * - logo: Logo clicked
     * - user-profile: User profile button clicked
     * - feedback: Feedback form
     * - notification: Notification button clicked
     * - notification-delete: Notification has been deleted by the user
     * - notification-download-report: Report was downloaded from the notification section
     */
    private static readonly CATEGORY_HEADER = "header";
    public static readonly GLOBAL_LOCALISATION_ENABLE = new AnalyticEvent("global-localisation-enabled", AnalyticEvent.CATEGORY_HEADER, AnalyticType.ANALYTIC);
    public static readonly GLOBAL_LOCALISATION_DISABLE = new AnalyticEvent("global-localisation-disabled", AnalyticEvent.CATEGORY_HEADER, AnalyticType.ANALYTIC);
    public static readonly HEADER_HAMBURGER = new AnalyticEvent("hamburger", AnalyticEvent.CATEGORY_HEADER, AnalyticType.ANALYTIC);
    public static readonly HEADER_LOGO = new AnalyticEvent("logo", AnalyticEvent.CATEGORY_HEADER, AnalyticType.ANALYTIC);
    public static readonly HEADER_PROFILE = new AnalyticEvent("user-profile", AnalyticEvent.CATEGORY_HEADER, AnalyticType.ANALYTIC);
    public static readonly HEADER_FEEDBACK = new AnalyticEvent("feedback", AnalyticEvent.CATEGORY_HEADER, AnalyticType.ANALYTIC);
    public static readonly HEADER_NOTIFICATION = new AnalyticEvent("notification", AnalyticEvent.CATEGORY_HEADER, AnalyticType.ANALYTIC);
    public static readonly HEADER_NOTIFICATION_DELETE = new AnalyticEvent("notification-delete", AnalyticEvent.CATEGORY_HEADER, AnalyticType.ANALYTIC);
    public static readonly HEADER_NOTIFICATION_DOWNLOAD_REPORT = new AnalyticEvent("notification-download-report", AnalyticEvent.CATEGORY_HEADER, AnalyticType.ANALYTIC);


    /**
     * User session/profile
     * - logout: user logout
     * - login: User entered username and password
     * - profile-completion-requested: Profile completion page displayed
     * - profile-completed: User entered required details on profile completion page
     */
    private static readonly CATEGORY_USER = "user";
    public static readonly USER_LOGOUT = new AnalyticEvent("logout", AnalyticEvent.CATEGORY_USER, AnalyticType.ANALYTIC);
    public static readonly USER_LOGIN = new AnalyticEvent("login", AnalyticEvent.CATEGORY_USER, AnalyticType.ANALYTIC);
    public static readonly USER_PROFILE_COMPLETION_REQUESTED = new AnalyticEvent("profile-completion-requested", AnalyticEvent.CATEGORY_USER, AnalyticType.ANALYTIC);
    public static readonly USER_PROFILE_COMPLETED = new AnalyticEvent("profile-completed", AnalyticEvent.CATEGORY_USER, AnalyticType.ANALYTIC);


    /**
     * Reports
     * - generate
     */
    private static readonly CATEGORY_REPORT = "report";
    public static readonly GENERATE_REPORT = new AnalyticEvent("generate",  AnalyticEvent.CATEGORY_REPORT, AnalyticType.ANALYTIC);

    /**
     * Search screen
     * - search-view-row: view a row on search results. Visible in extended search
     * - search-delete: bin icon
     * - search-discontinue: bin icon
     * - search-recontinue: recontinue icon
     * - search-change-page: Change page in search results either by next, previous, number, or items per page
     * - search-column-sort: Click a column heading to sort
     * - search-select-single: Quick single select button
     * - search-add-new: new entity button clicked
     * - search-add-new-extended: Add new when part of an extended search
     * - search-localisation-filter-updated: Localisation filters updated
     * - search-localisation-opened: Globe icon pressed
     * - search-add-pill: Add pill from more selection
     */
    private static readonly CATEGORY_ENTITY_SEARCH = "entity-search";
    public static readonly SEARCH_VIEW_ROW = new AnalyticEvent("search-view-row", AnalyticEvent.CATEGORY_ENTITY_SEARCH, AnalyticType.ANALYTIC, true, true);
    public static readonly SEARCH_DELETE_ROW = new AnalyticEvent("search-delete", AnalyticEvent.CATEGORY_ENTITY_SEARCH, AnalyticType.ANALYTIC, true);
    public static readonly SEARCH_DISCONTINUE_ROW = new AnalyticEvent("search-discontinue", AnalyticEvent.CATEGORY_ENTITY_SEARCH, AnalyticType.ANALYTIC, true);
    public static readonly SEARCH_RECONTINUE_ROW = new AnalyticEvent("search-recontinue", AnalyticEvent.CATEGORY_ENTITY_SEARCH, AnalyticType.ANALYTIC, true);
    public static readonly SEARCH_CHANGE_PAGE = new AnalyticEvent("search-change-page", AnalyticEvent.CATEGORY_ENTITY_SEARCH, AnalyticType.ANALYTIC, true);
    public static readonly SEARCH_COLUMN_SORT = new AnalyticEvent("search-column-sort", AnalyticEvent.CATEGORY_ENTITY_SEARCH, AnalyticType.ANALYTIC, true);
    public static readonly SEARCH_SELECT_SINGLE = new AnalyticEvent("search-select-single", AnalyticEvent.CATEGORY_ENTITY_SEARCH, AnalyticType.ANALYTIC, true);
    public static readonly SEARCH_ADD_NEW = new AnalyticEvent("search-add-new", AnalyticEvent.CATEGORY_ENTITY_SEARCH, AnalyticType.ANALYTIC, true);
    public static readonly SEARCH_ADD_NEW_EXTENDED = new AnalyticEvent("search-add-new-extended", AnalyticEvent.CATEGORY_ENTITY_SEARCH, AnalyticType.ANALYTIC, true);
    public static readonly SEARCH_FILTER_UPDATED = new AnalyticEvent("search-filter-updated", AnalyticEvent.CATEGORY_ENTITY_SEARCH, AnalyticType.ANALYTIC, true);
    public static readonly SEARCH_LOCALISATION_FILTER_UPDATED = new AnalyticEvent("search-localisation-filter-updated", AnalyticEvent.CATEGORY_ENTITY_SEARCH, AnalyticType.ANALYTIC, true);
    public static readonly SEARCH_LOCALISATION_FILTER_OPEN = new AnalyticEvent("search-localisation-opened", AnalyticEvent.CATEGORY_ENTITY_SEARCH, AnalyticType.ANALYTIC, true);
    public static readonly SEARCH_ADD_PILL = new AnalyticEvent("search-add-pill", AnalyticEvent.CATEGORY_ENTITY_SEARCH, AnalyticType.ANALYTIC, true);


    private static readonly CATEGORY_SEARCH_TIMING = "entity-search-timing";
    public static readonly SEARCH_TIMING_LOAD = new AnalyticEvent("load", AnalyticEvent.CATEGORY_SEARCH_TIMING, AnalyticType.ANALYTIC);

    private static readonly CATEGORY_ENTITY_TIMING = "entity-edit-timing";
    public static readonly ENTITY_TIMING_LOAD = new AnalyticEvent("load", AnalyticEvent.CATEGORY_ENTITY_TIMING, AnalyticType.ANALYTIC);
    public static readonly ENTITY_TIMING_ADD = new AnalyticEvent("add", AnalyticEvent.CATEGORY_ENTITY_TIMING, AnalyticType.ANALYTIC);
    public static readonly ENTITY_TIMING_EDIT = new AnalyticEvent("update", AnalyticEvent.CATEGORY_ENTITY_TIMING, AnalyticType.ANALYTIC);

    /**
     *
     * @param {string} action Action performed
     * @param {string} category Top level category that groups the events
     * @param {AnalyticType} type Additional information about how the event should be handled
     * @param {boolean} addPageToLabel Indicates if the current page should be prefixed on label to provide more context
     * @param {boolean} dialogAction Indicates if the action typically opens a dialog
     */
    constructor(public action: string, public category: string, public type: AnalyticType, public addPageToLabel?: boolean, public dialogAction?: boolean) {
    }
}
